import { Injectable } from "@angular/core";
import { ErrorMessageService } from "app/core/error-message.service";
import { ApiService } from "app/shared/security/api-services/api.service";
import { environment } from "environments/environment";
import { Observable, throwError as observableThrowError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { IApiErrorSuccessDTO } from "../../models/apiErrorSuccess";
import { EventRequestById } from "../../models/event";
import { RequestSourceTypeEnum } from "../../models/request";
import { EventApi } from "./event-api.models";

export interface IEvent {
  type?: EventTypesEnum;
  properties?: IEventPropertiesDTO;
}

export enum EventTypesEnum {
  TAKE_IN_CHARGE = "take-in-charge",
  CANCEL_TAKE_IN_CHARGE = "cancel-take-in-charge",
  CHANGE_RESPONSIBLE = "change-responsible",
  CHANGE_ACTIVITY = "change-activity",
  CLOSE_WORKORDER = "close-workorder",
}

export interface IMediaPayload {
  type: string;
  filecontent: string;
  origin: string;
}

export interface IEventPropertiesDTO {
  requestIds: number[];
  requestType?: RequestSourceTypeEnum;
  responsibleUser?: string;
  activityCode?: string;
  activityTemplateId?: number;
  responsibleUnitId?: number;
  responsibleGroupId?: number;
  reason?: string;
  internalComment?: string;
  photos?: IMediaPayload[];
}

@Injectable()
export class EventApiService extends EventApi {
  private _workorderEndpointPath: string =
    environment.apiHost + "/v1/workorders/";
  private _workorderEventsEndpointPath: string =
    environment.apiHost + "/v1/workorders/events";

  constructor(
    private apiService: ApiService,
    private errorMessageService: ErrorMessageService,
  ) {
    super();
  }

  public getEventsByWorkorderId(id: number): Observable<EventRequestById[]> {
    const endPoint = `${this._workorderEndpointPath}${id}/events`;
    return this.apiService.get<{ items: EventRequestById[] }>(endPoint).pipe(
      map((res) => res.items),
      tap(
        (res) => res,
        (error) => this.errorHandler(error),
      ),
    );
  }

  public addEventByWorkorderId(
    id: number,
    event: EventRequestById,
  ): Observable<EventRequestById> {
    const endPoint = `${this._workorderEndpointPath}${id}/events`;
    return this.apiService
      .post<EventRequestById, EventRequestById>(endPoint, event)
      .pipe(
        map((bs) => bs.body),
        tap(
          (res) => res,
          (error) => this.errorHandler(error),
        ),
      );
  }

  public postTakeInCharge(
    ids: number[],
    responsibleUser?: string,
  ): Observable<IApiErrorSuccessDTO> {
    const body: IEvent = {
      type: EventTypesEnum.TAKE_IN_CHARGE,
      properties: { requestIds: ids },
    };
    if (responsibleUser) {
      body.properties.responsibleUser = responsibleUser;
    }
    const thisPost = this.apiService.post(
      this._workorderEventsEndpointPath,
      body,
    );
    // eslint-disable-next-line
    return thisPost.pipe.apply(thisPost, this.handleInfoOrErrorOperator());
  }

  public postCancelTakeInCharge(
    ids: number[],
  ): Observable<IApiErrorSuccessDTO> {
    const body: IEvent = {
      type: EventTypesEnum.CANCEL_TAKE_IN_CHARGE,
      properties: { requestIds: ids },
    };
    const thisPost = this.apiService.post(
      this._workorderEventsEndpointPath,
      body,
    );
    // eslint-disable-next-line
    return thisPost.pipe.apply(thisPost, this.handleInfoOrErrorOperator());
  }

  public postChangeActivity(
    eventProperties: IEventPropertiesDTO,
  ): Observable<IApiErrorSuccessDTO> {
    const body: IEvent = {
      type: EventTypesEnum.CHANGE_ACTIVITY,
      properties: eventProperties,
    };
    const thisPost = this.apiService.post(
      this._workorderEventsEndpointPath,
      body,
    );
    // eslint-disable-next-line
    return thisPost.pipe.apply(thisPost, this.handleInfoOrErrorOperator());
  }

  public postChangeResponsible(
    ids: number[],
    responsibleUser: string,
    requestType: RequestSourceTypeEnum,
  ): Observable<IApiErrorSuccessDTO> {
    const body: IEvent = {
      type: EventTypesEnum.CHANGE_RESPONSIBLE,
      properties: { requestIds: ids, responsibleUser, requestType },
    };
    const thisPost = this.apiService.post(
      this._workorderEventsEndpointPath,
      body,
    );
    // eslint-disable-next-line
    return thisPost.pipe.apply(thisPost, this.handleInfoOrErrorOperator());
  }

  public postCloseWorkorder(
    eventProperties: IEventPropertiesDTO,
  ): Observable<IApiErrorSuccessDTO> {
    const body: IEvent = {
      type: EventTypesEnum.CLOSE_WORKORDER,
      properties: eventProperties,
    };
    const thisPost = this.apiService.post(
      this._workorderEventsEndpointPath,
      body,
    );
    // eslint-disable-next-line
    return thisPost.pipe.apply(thisPost, this.handleInfoOrErrorOperator());
  }

  private errorHandler(error) {
    this.errorMessageService.notifyError(error);
    return observableThrowError(() => error);
  }

  private infoHandler(error: IApiErrorSuccessDTO, status: number): void {
    this.errorMessageService.notifyInfo(error, status);
  }

  private handleInfoOrErrorOperator() {
    return [
      tap((r: { body: IApiErrorSuccessDTO; status: number }) => {
        if (r.status == 207) {
          this.infoHandler(r?.body, r.status);
        }
      }),
      map((r: { body: any; status: number }) => r.body),
      catchError((error) => this.errorHandler(error)),
    ];
  }
}
